import { useState, useMemo } from "react";
import { sourcePerformanceData, accountPerformanceData } from "@/mocks/reportsData";

interface Props {
  onExport?: () => void;
}

export default function LeadSourcePerformance({ onExport }: Props) {
  const [viewMode, setViewMode] = useState<"source" | "account">("source");

  const maxSourceCount = sourcePerformanceData[0].count;
  const maxAccountCount = Math.max(...accountPerformanceData.map((a) => a.count));

  // Group accounts by source for visual grouping
  const groupedAccounts = useMemo(() => {
    const groups: Record<string, typeof accountPerformanceData> = {};
    accountPerformanceData.forEach((a) => {
      if (!groups[a.sourceGroup]) groups[a.sourceGroup] = [];
      groups[a.sourceGroup].push(a);
    });
    return groups;
  }, []);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Lead Source Performance</h3>
        {/* Toggle + Export */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode("source")}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                viewMode === "source"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-transparent text-gray-600 hover:text-gray-800"
              }`}
            >
              By Source
            </button>
            <button
              onClick={() => setViewMode("account")}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                viewMode === "account"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-transparent text-gray-600 hover:text-gray-800"
              }`}
            >
              By Account
            </button>
          </div>
          {onExport && (
            <button
              onClick={onExport}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer"
              title="Export CSV"
            >
              <i className="ri-download-line text-sm" />
            </button>
          )}
        </div>
      </div>

      {viewMode === "source" ? (
        /* ── BY SOURCE VIEW (existing) ── */
        <div className="space-y-3">
          {sourcePerformanceData.map((item) => (
            <div key={item.source} className="flex items-center gap-3">
              <span className="w-[100px] text-xs text-gray-600 flex-shrink-0 whitespace-nowrap">{item.source}</span>
              <div className="flex-1 min-w-0">
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${item.color} rounded-full transition-all duration-500`}
                    style={{ width: `${Math.max((item.count / maxSourceCount) * 100, 2)}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-gray-700 flex-shrink-0 w-[80px] text-right whitespace-nowrap">
                {item.count} <span className="text-gray-400">{item.percentage}%</span>
              </span>
            </div>
          ))}
        </div>
      ) : (
        /* ── BY ACCOUNT VIEW ── */
        <div className="space-y-3">
          {Object.entries(groupedAccounts).map(([group, accounts]) => (
            <div key={group}>
              {/* Source group header */}
              <p className="text-xs font-semibold text-gray-700 mb-1.5">{group}</p>
              {accounts.map((item, idx) => {
                const isSubAccount = accounts.length > 1 && idx > 0;
                return (
                  <div key={item.account} className={`flex items-center gap-3 ${isSubAccount ? "pl-4" : ""}`}>
                    <span className={`text-xs text-gray-500 flex-shrink-0 whitespace-nowrap ${isSubAccount ? "w-[90px]" : "w-[100px]"}`}>
                      {item.account}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${item.color} rounded-full transition-all duration-500`}
                          style={{ width: `${Math.max((item.count / maxAccountCount) * 100, 2)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-gray-700 flex-shrink-0 w-[80px] text-right whitespace-nowrap">
                      {item.count} <span className="text-gray-400">{item.percentage}%</span>
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}